import { computed, effect, inject, Injectable } from '@angular/core';
import Highcharts from 'highcharts/highstock';
import {
  LANGUAGE_LOCALE_MAP,
  LANGUAGES,
  type Language,
  type Locale
} from 'src/app/core/constants/language-and-locale-constants';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarNotificationService } from '@shared/services/snackbar-notification.service';
import { type Observable } from 'rxjs';

const DEFAULT_LANGUAGE = LANGUAGES[0];
const LANGUAGE_LOCAL_STORAGE_KEY = 'language';

const LANGUAGE_LOAD_ERROR_MESSAGES = {
  en: 'Could not load the translations. Please refresh the page.',
  de: 'Die Übersetzungen konnten nicht geladen werden. Bitte laden Sie die Seite neu.',
  fr: 'Impossible de charger les traductions. Veuillez actualiser la page.',
  nl: 'Kan de vertalingen niet laden. Vernieuw de pagina.'
} as const satisfies Record<Language, string>;

@Injectable({ providedIn: 'root' })
export class LanguageService {
  private translateService = inject(TranslateService);
  private notificationService = inject(SnackbarNotificationService);

  public language = computed<Language>(
    () => LANGUAGES.find(language => language === this.translateService.currentLang()) ?? DEFAULT_LANGUAGE
  );
  public locale = computed<Locale>(() => LANGUAGE_LOCALE_MAP[this.language()]);

  constructor() {
    this.persistLanguage();
    this.updateChartsLanguage();
  }

  public initialize(): Observable<unknown> {
    const storedLanguage = localStorage.getItem(LANGUAGE_LOCAL_STORAGE_KEY);
    const initialLanguage = LANGUAGES.find(language => language === storedLanguage) ?? DEFAULT_LANGUAGE;
    return this.translateService.use(initialLanguage);
  }

  public setLanguage(language: Language): void {
    this.translateService.use(language).subscribe({
      error: () => this.notificationService.text(LANGUAGE_LOAD_ERROR_MESSAGES[language], 'error')
    });
  }

  private persistLanguage() {
    effect(() => localStorage.setItem(LANGUAGE_LOCAL_STORAGE_KEY, this.language()));
  }

  private updateChartsLanguage() {
    effect(() => {
      Highcharts.setOptions({ lang: { locale: this.locale() } });
      Highcharts.charts.forEach(chart => chart?.update({ lang: { locale: this.locale() } }));
    });
  }
}
